import React from 'react';

import { EntityCriticalityConfigListFragment } from 'core/graphql';
import { Avatar } from 'shared/components/elements/Avatar';

import css from '../SearchFilter.module.scss';
import {
  booleanFieldDefaults,
  FieldTypes,
  globalOperators,
  ipFieldDefaults,
  keyValueFieldDefaults,
  listFieldDefaults,
  pastDateFieldDefaults,
  textFieldDefaults,
} from '../types';
import { UserType } from './insight';

export const getEntityConfigurationGroupFields = (
  criticalities: EntityCriticalityConfigListFragment[],
  users: UserType[]
): FieldTypes[] => {
  const userOptions = [...users]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((user) => ({
      prop: user.username,
      label: user.displayName,
      group: 'Users',
      listTemplate: (label) => (
        <div>
          <Avatar className={css.listAvatar} email={user.email} size='small' />
          <span style={{ verticalAlign: 'middle' }}>{label}</span>
        </div>
      ),
    }));

  return [
    {
      ...pastDateFieldDefaults,
      prop: 'created',
      label: 'Created',
      source: 'entity',
    },
    {
      ...listFieldDefaults,
      source: 'entity',
      prop: 'createdBy',
      label: 'Created By',
      operators: [...globalOperators],
      options: userOptions,
    },
    {
      ...listFieldDefaults,
      label: 'Criticality',
      prop: 'criticality',
      operators: [...globalOperators],
      source: 'entity',
      options: [
        {
          prop: null,
          label: 'Default',
          listTemplate: (label) => <span>{label}</span>,
        },
        ...[...criticalities]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((criticality) => ({
            prop: criticality.name,
            label: criticality.name,
            group: 'Criticality Values',
            listTemplate: (label) => <span>{label}</span>,
          })),
      ],
    },
    {
      ...listFieldDefaults,
      label: 'Configuration Type',
      operators: [...globalOperators],
      options: [
        {
          prop: 'INVENTORY',
          listTemplate: (label) => <span>{label}</span>,
          label: 'Inventory',
        },
        {
          prop: 'VALUE',
          listTemplate: (label) => <span>{label}</span>,
          label: 'Value',
        },
      ],
      prop: 'configurationType',
      source: 'entity',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'description',
      label: 'Description',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'entityNamespace',
      label: 'Entity Namespace',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'entityType',
      label: 'Entity Type',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'groups',
      label: 'Groups',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'inventoryType',
      label: 'Inventory Type',
    },
    {
      ...pastDateFieldDefaults,
      source: 'entity',
      prop: 'lastUpdated',
      label: 'Last Updated',
    },
    {
      ...listFieldDefaults,
      source: 'entity',
      prop: 'lastUpdatedBy',
      label: 'Last Updated By',
      operators: [...globalOperators],
      options: userOptions,
    },
    {
      ...textFieldDefaults,
      prop: 'name',
      label: 'Name',
      source: 'entity',
    },
    {
      ...ipFieldDefaults,
      source: 'entity',
      prop: 'networkBlock',
      label: 'Network Block',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'prefix',
      label: 'Prefix',
    },
    {
      ...textFieldDefaults,
      source: 'entity',
      prop: 'suffix',
      label: 'Suffix',
    },
    {
      ...keyValueFieldDefaults,
      label: 'Tags',
      prop: 'tag',
      source: 'entity',
    },
    {
      ...booleanFieldDefaults,
      label: 'Suppressed',
      prop: 'suppressed',
      source: 'entity',
    },
  ];
};
